import React, { useMemo } from "react"

import moment from "moment-timezone"
import * as PapaParse from "papaparse"
import { CSVLink } from "react-csv"

import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ButtonElement } from "@front-shared/elements/button.element"
import { PageFloatingButton } from "@front-shared/elements/pageFloatingButton.element"

import {
    CHECKER_CONFIG,
    CoverageCheckerMode,
} from "../../config/checker.config"
import { CSV_CHECKER_CONFIG } from "../../config/csvChecker.config"
import { GENERAL_CONFIG } from "../../config/general.config"
import { CsvHelper } from "../../helpers/csv.helper"
import { ExportReportHelper } from "../../helpers/exportReport.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { useAppSelector } from "../../store/selectors/app.selector"
import { checkerSelector } from "../../store/selectors/checker.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import type {
    IContinuousMonitoringCoverageCheck,
    IMedicaidCoverageCheck,
} from "../../types/objects.types"

import downloadWhiteImg from "@front-shared/assets/images/icons/download-white.svg"

export const ResultsDownloadComponent: React.FunctionComponent<
    IResultsDownloadComponentProps
> = props => {
    const checkerState = useAppSelector(checkerSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const checksType = useMemo<QuotaUsageType | undefined>(() => {
        return checkerState.checkerMode === CoverageCheckerMode.MANUAL
            ? checkerState.manualCheckType
            : checkerState.csvCheckType
    }, [
        checkerState.csvCheckType,
        checkerState.manualCheckType,
        checkerState.checkerMode,
    ])

    const resultsCount = useMemo<number>(() => {
        switch (checksType) {
            case QuotaUsageType.GENERAL:
            case QuotaUsageType.DISCOVER:
            case QuotaUsageType.SMART_SCAN:
            case QuotaUsageType.AGENTIC_PORTAL:
            case QuotaUsageType.AGENTIC_PHONE:
                return (
                    checkerState.continuousMonitoringCoverageChecks?.reduce(
                        (accumulator, item) =>
                            accumulator +
                            (item.result?.IN ? 1 : 0) +
                            (item.result?.OUT ? 1 : 0),
                        0,
                    ) || 0
                )

            case QuotaUsageType.MEDICAID:
                return checkerState.medicaidChecks.filter(item => item.result)
                    .length

            default:
                return 0
        }
    }, [
        checksType,
        checkerState.medicaidChecks,
        checkerState.continuousMonitoringCoverageChecks,
    ])

    const getProcessedCsvResults = (): string => {
        let coverageChecks:
            | IContinuousMonitoringCoverageCheck[]
            | IMedicaidCoverageCheck[]
            | undefined

        switch (checksType) {
            case QuotaUsageType.GENERAL:
            case QuotaUsageType.DISCOVER:
            case QuotaUsageType.SMART_SCAN:
            case QuotaUsageType.AGENTIC_PORTAL:
            case QuotaUsageType.AGENTIC_PHONE:
                coverageChecks = checkerState.continuousMonitoringCoverageChecks

                break

            case QuotaUsageType.MEDICAID:
                coverageChecks = checkerState.medicaidChecks

                break
        }

        if (!coverageChecks?.length || !resultsCount || !checksType) {
            return ""
        }

        const csvData = [] as (string | number | boolean)[][]

        let headersAdded = false

        // Since our indexes can be messed up in state array (because removing adding rows)
        // We'd like to get indexes in similar manner how we are doing in manual checker component
        const checksIndexes = UtilHelper.getRealIndexesOfArray(coverageChecks)

        // Go through each index of our checks, so basically go through each check input and result data
        checksIndexes.forEach(index => {
            const inputData = coverageChecks?.[index].inputData

            if (!inputData) {
                return
            }

            let resultsData: (
                | IOneVerifyCoverageCheckHistory
                | undefined
                | null
            )[] = []

            switch (checksType) {
                case QuotaUsageType.GENERAL:
                case QuotaUsageType.DISCOVER:
                case QuotaUsageType.SMART_SCAN:
                case QuotaUsageType.AGENTIC_PORTAL:
                case QuotaUsageType.AGENTIC_PHONE:
                    resultsData = [
                        // @ts-ignore
                        coverageChecks?.[index].result?.[
                            PayerCoverageCheckNetwork.IN
                        ],

                        // @ts-ignore
                        coverageChecks?.[index].result?.[
                            PayerCoverageCheckNetwork.OUT
                        ],
                    ]

                    break

                case QuotaUsageType.MEDICAID:
                    resultsData = [
                        coverageChecks?.[index]?.result as
                            | IOneVerifyCoverageCheckHistory
                            | undefined
                            | null,
                    ]

                    break
            }

            const columns = CsvHelper.getCoverageCheckerDownloadCSVColumns(
                selectedPracticeRole?.availableModalities,
                checksType,
            )

            resultsData.forEach(coverageCheck => {
                if (!coverageCheck) {
                    return
                }

                if (
                    props.includeInputIds &&
                    !props.includeInputIds.includes(inputData.id)
                ) {
                    return
                }

                // We need to process headers here, so we add passThrough headers as well
                if (!headersAdded) {
                    // Add header rows
                    // Include also pass through if they are there
                    csvData.push([
                        ...columns.map(
                            mapping =>
                                mapping.checkerExportLabel || mapping.label,
                        ),

                        ...Object.keys(inputData.passThroughColumns || {}),
                    ])

                    headersAdded = true
                }

                let csvRow = [] as (string | number | boolean)[]

                // Go through each column for our final csv and process check using mappings
                columns.forEach(column => {
                    // Add value to row
                    csvRow.push(
                        ExportReportHelper.getMappedColumnValue(
                            coverageCheck,
                            column,
                            "",
                            false,
                        ) as string,
                    )
                })

                // Add passThrough columns to the end of csv rows
                if (inputData.passThroughColumns) {
                    Object.keys(inputData.passThroughColumns).forEach(key => {
                        if (!inputData.passThroughColumns) {
                            return
                        }

                        csvRow.push(inputData.passThroughColumns[key])
                    })
                }

                // Process each value and make sure there are no commas...
                csvRow = csvRow.map(column =>
                    typeof column === "string"
                        ? column.replace(/,/g, "")
                        : column,
                )

                // Add row to csv
                csvData.push(csvRow)
            })
        })

        return PapaParse.unparse(csvData)
    }

    const processedCsvData = useMemo<string>(() => {
        return getProcessedCsvResults()
    }, [
        checksType,
        checkerState.continuousMonitoringCoverageChecks,
        checkerState.medicaidChecks,
        props.includeInputIds,
    ])

    const csvFileName = useMemo<string>(() => {
        return CSV_CHECKER_CONFIG.downloadFileName
            .replace(
                "{{time}}",
                moment().format(GENERAL_CONFIG.defaultMomentDateTimeFormat),
            )
            .replace(
                "{{checksType}}",
                CHECKER_CONFIG.manualTypeTabs.find(
                    item => item.key === checksType,
                )?.label || "",
            )
    }, [processedCsvData, checksType])

    const downloadLabel =
        props.label ||
        `Download CSV with ${resultsCount} result${
            resultsCount === 1 ? "" : "s"
        }`

    return resultsCount &&
        (!props.includeInputIds || props.includeInputIds.length) ? (
        <div
            className={`
                relative
                ${props.className || ""}
            `}
        >
            <CSVLink
                data={processedCsvData}
                filename={csvFileName}
                target="_blank"
            >
                {props.variant === "inline" ? (
                    <ButtonElement
                        type="primary"
                        htmlType="button"
                        size="large"
                        icon={downloadWhiteImg}
                        label={downloadLabel}
                    />
                ) : (
                    <PageFloatingButton
                        className="!bottom-[24px]"
                        label={downloadLabel}
                        zIndex={30}
                        icon={downloadWhiteImg}
                    />
                )}
            </CSVLink>
        </div>
    ) : null
}

interface IResultsDownloadComponentProps {
    className?: string
    variant?: "floating" | "inline"
    label?: string
    includeInputIds?: string[]
}
